import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Building2, Plus, Pencil, Trash2, X, Save, Clock, Calendar, Percent, Users, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const TIME_SLOTS = [
  "8:00 - 10:00", "9:00 - 11:00", "9:00 - 12:00", "10:00 - 12:00",
  "11:00 - 13:00", "12:00 - 14:00", "14:00 - 16:00", "15:00 - 17:00",
  "16:00 - 18:00", "17:00 - 19:00"
];

const AdminPartnerCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [companyUsers, setCompanyUsers] = useState({});

  const [formData, setFormData] = useState({
    name: "", delivery_day: "Lunedì", delivery_time_slot: "9:00 - 12:00", discount_percentage: 5
  });

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.get(`${API}/admin/partner-companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data);
    } catch (e) {
      toast.error("Errore nel caricamento aziende");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompanies(); }, []);

  const fetchUsers = async (companyId) => {
    if (companyUsers[companyId]) {
      setExpandedId(expandedId === companyId ? null : companyId);
      return;
    }
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.get(`${API}/admin/partner-companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyUsers(prev => ({ ...prev, [companyId]: res.data }));
      setExpandedId(companyId);
    } catch (e) {
      toast.error("Errore nel caricamento utenti");
    }
  };

  const openCreate = () => {
    setFormData({ name: "", delivery_day: "Lunedì", delivery_time_slot: "9:00 - 12:00", discount_percentage: 5 });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (company) => {
    setFormData({
      name: company.name,
      delivery_day: company.delivery_day,
      delivery_time_slot: company.delivery_time_slot,
      discount_percentage: company.discount_percentage
    });
    setEditingId(company.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error("Inserisci il nome dell'azienda"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      if (editingId) {
        await axios.put(`${API}/admin/partner-companies/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Azienda aggiornata");
      } else {
        await axios.post(`${API}/admin/partner-companies`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Azienda creata");
      }
      setShowForm(false);
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Eliminare "${company.name}"?\nGli utenti associati verranno scollegati.`)) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/partner-companies/${company.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`"${company.name}" eliminata`);
      fetchCompanies();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin/ordini" className="text-green-500 hover:underline text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Building2 className="w-7 h-7 text-blue-500" />
              Aziende Partner
            </h1>
          </div>
          <button
            onClick={openCreate}
            className="bg-green-500 text-black px-4 py-2.5 rounded-lg font-bold hover:bg-green-400 flex items-center gap-2 transition-colors"
            data-testid="add-partner-btn"
          >
            <Plus className="w-5 h-5" /> Nuova Azienda
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Caricamento...</div>
        ) : companies.length === 0 ? (
          <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-lg">
            <Building2 className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400">Nessuna azienda partner configurata</p>
            <p className="text-zinc-600 text-sm mt-1">Crea la prima azienda partner per iniziare</p>
          </div>
        ) : (
          <div className="space-y-3">
            {companies.map(company => (
              <div key={company.id} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden hover:border-zinc-700 transition-all">
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">{company.name}</h3>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm">
                        <span className="flex items-center gap-1.5 text-zinc-400">
                          <Calendar className="w-4 h-4 text-blue-500" />
                          {company.delivery_day}
                        </span>
                        <span className="flex items-center gap-1.5 text-zinc-400">
                          <Clock className="w-4 h-4 text-yellow-500" />
                          {company.delivery_time_slot}
                        </span>
                        <span className="flex items-center gap-1.5 text-zinc-400">
                          <Percent className="w-4 h-4 text-green-500" />
                          Sconto {company.discount_percentage}%
                        </span>
                        <button
                          onClick={() => fetchUsers(company.id)}
                          className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 transition-colors"
                          data-testid={`users-btn-${company.id}`}
                        >
                          <Users className="w-4 h-4" />
                          {company.users_count || 0} utenti
                          {expandedId === company.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button onClick={() => openEdit(company)} className="bg-green-500/20 hover:bg-green-500/40 text-green-500 p-2 rounded-lg transition-colors" title="Modifica">
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button onClick={() => handleDelete(company)} className="bg-red-500/20 hover:bg-red-500/40 text-red-500 p-2 rounded-lg transition-colors" title="Elimina">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {expandedId === company.id && companyUsers[company.id] && (
                  <div className="border-t border-zinc-800 p-4 bg-zinc-800/50">
                    <h4 className="text-sm font-semibold text-zinc-400 mb-2">Utenti associati</h4>
                    {companyUsers[company.id].length === 0 ? (
                      <p className="text-zinc-500 text-sm">Nessun utente associato</p>
                    ) : (
                      <div className="space-y-1">
                        {companyUsers[company.id].map(user => (
                          <div key={user.id} className="flex items-center justify-between py-1.5 text-sm">
                            <span className="text-white">{user.name}</span>
                            <span className="text-zinc-500">{user.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full" data-testid="partner-form-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  {editingId ? "Modifica Azienda" : "Nuova Azienda Partner"}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-zinc-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Nome Azienda *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder="Es: Acme S.r.l."
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                    data-testid="partner-name-input"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Giorno di consegna</label>
                  <select
                    value={formData.delivery_day}
                    onChange={e => setFormData({...formData, delivery_day: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                    data-testid="partner-day-select"
                  >
                    {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Fascia oraria</label>
                  <select
                    value={formData.delivery_time_slot}
                    onChange={e => setFormData({...formData, delivery_time_slot: e.target.value})}
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                    data-testid="partner-time-select"
                  >
                    {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Sconto dedicato (%)</label>
                  <input
                    type="number"
                    value={formData.discount_percentage}
                    onChange={e => setFormData({...formData, discount_percentage: parseFloat(e.target.value) || 0})}
                    min="0" max="50" step="0.5"
                    className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                    data-testid="partner-discount-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowForm(false)} className="flex-1 bg-zinc-800 text-white py-3 rounded-lg font-semibold hover:bg-zinc-700 transition-colors">
                  Annulla
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 disabled:bg-zinc-700 disabled:text-zinc-500 flex items-center justify-center gap-2 transition-colors"
                  data-testid="save-partner-btn"
                >
                  {saving ? "Salvataggio..." : <><Save className="w-5 h-5" /> Salva</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPartnerCompanies;
